export interface Profile {
  version: 1;
  unlocked: [boolean, boolean, boolean];
  bestAccuracy: [number, number, number, number];
  bestOpponentScore: [number, number, number, number];
  perfectMedals: [boolean, boolean, boolean, boolean];
  sound: boolean;
  taunts: boolean;
}
export interface MatchResult {
  accuracy: number;
  perfect: boolean;
  opponentScore: number;
  practice: boolean;
}
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}
export const PROFILE_KEY = 'biplanes.profile';
function defaults(): Profile {
  return {
    version: 1,
    unlocked: [false, false, false],
    bestAccuracy: [0, 0, 0, 0],
    bestOpponentScore: [10, 10, 10, 10],
    perfectMedals: [false, false, false, false],
    sound: true,
    taunts: true,
  };
}
function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
export class ProfileStore {
  readonly profile: Profile;
  constructor(private readonly storage: StorageLike | null = browserStorage()) {
    const p = defaults();
    try {
      const raw = storage?.getItem(PROFILE_KEY);
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          const v = parsed as Record<string, unknown>;
          for (const key of ['unlocked', 'perfectMedals'] as const) {
            const value = v[key];
            if (
              Array.isArray(value) &&
              value.length === p[key].length &&
              value.every((x) => typeof x === 'boolean')
            )
              Object.assign(p[key], value);
          }
          for (const key of ['bestAccuracy', 'bestOpponentScore'] as const) {
            const value = v[key];
            const max = key === 'bestAccuracy' ? 100 : 10;
            if (
              Array.isArray(value) &&
              value.length === 4 &&
              value.every((x) => Number.isInteger(x) && x >= 0 && x <= max)
            )
              Object.assign(p[key], value);
          }
          if (typeof v['sound'] === 'boolean') p.sound = v['sound'];
          if (typeof v['taunts'] === 'boolean') p.taunts = v['taunts'];
        }
      } else {
        const progress = Number(storage?.getItem('biplanes.progress'));
        if (Number.isInteger(progress) && progress >= 1 && progress <= 4)
          for (let i = 0; i < progress - 1; i++) p.unlocked[i] = true;
      }
    } catch {}
    this.profile = p;
  }
  isUnlocked(pilot: number): boolean {
    return pilot === 0 || (pilot >= 1 && pilot <= 3 && this.profile.unlocked[pilot - 1] === true);
  }
  save(): void {
    try {
      this.storage?.setItem(PROFILE_KEY, JSON.stringify(this.profile));
    } catch {}
  }
  setSound(enabled: boolean): void {
    this.profile.sound = enabled;
    this.save();
  }
  setTaunts(enabled: boolean): void {
    this.profile.taunts = enabled;
    this.save();
  }
  recordWin(opponent: number, result: MatchResult): void {
    if (!Number.isInteger(opponent) || opponent < 0 || opponent > 3 || result.practice) return;
    if (opponent < 3) this.profile.unlocked[opponent] = true;
    this.profile.bestAccuracy[opponent] = Math.max(
      this.profile.bestAccuracy[opponent]!,
      Math.min(100, Math.max(0, Math.trunc(result.accuracy))),
    );
    this.profile.bestOpponentScore[opponent] = Math.min(
      this.profile.bestOpponentScore[opponent]!,
      Math.max(0, Math.trunc(result.opponentScore)),
    );
    this.profile.perfectMedals[opponent] ||= result.perfect;
    this.save();
  }
}
export const profileStore = new ProfileStore();
